import {
  etherToWei,
  ticketDateFormat,
  formatNumber,
} from "../../../../utils/common";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import dayjs from "dayjs";
import StepsModel from "../StepsModel";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/solid";
import { Crown, MaskedLogo } from "../../../../assets";
import NewTabIcon from "../../../../assets/svg/NewTabIcon";
import { DateTime } from "luxon";
import { contractAddress, defaultAddress } from "../../../../config";
import { useHookstate } from "@hookstate/core";
import { metaMaskState } from "../../../../hooks/store";
import swssDrawABI from "../../../../abi/swssDraw";
import { useAppKitProvider } from "@reown/appkit/react";
import {
  checkWalletLocked,
  checkToken,
  approveToken,
  purchaseDrawTicket,
} from "../../../../service/walletConnectService";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import styles from "../styles";
import theme from "../../../../theme/Theme";

const DrawTicket = ({ drawData }) => {
  let timerInterval;
  const [smartContractLink, setSmartContractLink] = useState("");
  const [ticketQty, setTicketQty] = useState(1);
  const [isExpired, setIsExpired] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFutureDate, setIsFutureDate] = useState(true);
  const [soldTickets, setSoldTickets] = useState(0);
  const [remainingTickets, setRemainingTickets] = useState(0);
  const [remainingPercentage, setRemainingPercentage] = useState(0);
  const matches = useMediaQuery(theme.breakpoints.up("sm"));
  const [progressBarWidth, setProgressBarWidth] = useState(0);
  const [drawDetailsModule, setDrawDetailsModule] = useState({});
  const walletConnectState = useHookstate(metaMaskState);
  const { walletProvider } = useAppKitProvider("eip155");
  const [progressData, setProgressData] = useState({
    steps: [],
    currentStep: 0,
    timer: 0,
  });
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [configData, setConfigData] = useState({
    ticketPrice: 0,
    startTime: "",
    endTime: "",
    maxParticipants: 0,
    withdrawalFee: 0,
  });

  useEffect(() => {
    if (drawData) {
      fetchDrawData();
    }
  }, [drawData]);

  const checkIfFutureDate = (date) => {
    const now = DateTime.now();
    const inputDate = DateTime.fromISO(date);
    return inputDate > now;
  };

  useEffect(() => {
    if (configData.startTime) {
      const isAfterDate = checkIfFutureDate(configData.startTime);
      setIsFutureDate(isAfterDate);
    }
    const timer = setInterval(calculateTimeLeft, 1000);
    // Cleanup on component unmount
    return () => clearInterval(timer);
  }, [configData]);

  const fetchDrawData = async () => {
    try {
      if (!drawData) {
        return <div>Draw not found</div>;
      }
      setDrawDetailsModule(drawData);
      console.log(drawDetailsModule);
      setSmartContractLink(
        `${drawData?.explorerBaseUrl}/address/${drawData.tokenAddress}`
      );
      const totalTickets = drawData.maxParticipants;

      setIsFutureDate(false);
      setConfigData({
        ticketPrice: drawData.ticketPrice,
        startTime: drawData.startTime,
        endTime: drawData.endTime,
        maxParticipants: drawData.maxParticipants,
      });

      const remainingTickets = totalTickets - drawData.participantCount;
      setRemainingPercentage(
        ((remainingTickets / totalTickets) * 100).toFixed(0)
      );
      const sold = drawData.participantCount;
      setSoldTickets(sold);
      setRemainingTickets(remainingTickets);
      const progressBarWidth = totalTickets ? (sold / totalTickets) * 100 : 0;
      setProgressBarWidth(progressBarWidth);
    } catch (error) {
      console.log("Error loading draw data:", error);
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleTicketPurchase = async () => {
    try {
      if (walletConnectState.get().isConnected) {
        if (Number(ticketQty) > 0 && Number(ticketQty) < 51) {
          if (isExpired) {
            toast.warn("Draw Completed.", {
              style: { fontSize: "14px" },
            });
            return;
          }
          const isWalletUnlocked = await checkWalletLocked(walletProvider);
          if (!isWalletUnlocked) {
            toast.warn("Please unlock your wallet to purchase tickets.", {
              style: { fontSize: "14px" },
            });
            return;
          }
          purchaseTicket(walletProvider);
        } else {
          toast.warn("You can purchase a maximum of 50 tickets per request.", {
            style: { fontSize: "14px" },
          });
          return;
        }
      } else {
        toast.warn("Please connect your wallet to purchase tickets.", {
          style: { fontSize: "14px" },
        });
      }
    } catch (error) {
      console.log("Error purchasing ticket:", error);
    }
  };

  const simulateNetworkDelay = (ms) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  // Execute steps and update progress
  const executeStep = async (stepIndex, stepFunction) => {
    setProgressData((prev) => ({ ...prev, currentStep: stepIndex }));
    await stepFunction();
  };

  const startCountdown = (duration) => {
    setProgressData((prev) => ({ ...prev, timer: duration }));
    let remainingTime = duration;

    timerInterval = setInterval(() => {
      remainingTime -= 1;
      setProgressData((prev) => ({ ...prev, timer: remainingTime }));

      if (remainingTime <= 0) {
        clearInterval(timerInterval); // Clear the interval
        setProgressData((prev) => ({ ...prev, timer: 0 })); // Explicitly set timer to 0
      }
    }, 1000);
  };

  const purchaseTicket = async (walletProvider) => {
    const steps = [
      "Approval Started",
      "Approval Done",
      "Transaction Started",
      "Minting NFT",
      "Done",
    ];
    const totalTimerDuration = 50; // Total timer duration in seconds

    try {
      const connectedAddress = Cookies.get("connectedAddress");
      if (!connectedAddress) {
        toast.warn("Please connect to a wallet to proceed.", {
          style: { fontSize: "14px" },
        });
        closeModal();
        return;
      }

      const referrer =
        Cookies.get("referrer")?.toLowerCase() ===
        connectedAddress.toLowerCase()
          ? defaultAddress
          : Cookies.get("referrer") || defaultAddress;

      const data = {
        drawId: drawData?.drawId,
        referrer,
        ticketCount: ticketQty,
        ticketPrice: drawData?.ticketPrice,
        tokenContractAddress: String(drawData.tokenAddress),
        blockchain: drawData.blockchain,
      };

      let result = { error: false };
      // Step 1: Approval
      await executeStep(0, async () => {
        if (data.tokenContractAddress !== defaultAddress) {
          const { status, message, contractData } = await checkToken(
            walletProvider,
            data.tokenContractAddress,
            drawData.blockchain
          );

          if (status === "success") {
            openModal();
            setProgressData({
              steps,
              currentStep: 0,
              timer: totalTimerDuration,
            });
            startCountdown(totalTimerDuration);
            const { status, message } = await approveToken(
              walletProvider,
              etherToWei(String(data.ticketPrice * ticketQty)),
              contractData.connectedAddress,
              contractData.tokenContract
            );

            if (status !== "success") {
              if (message?.includes("user rejected transaction")) {
                toast.error("User rejected transaction", {
                  style: { fontSize: "14px" },
                });
                closeModal();
                result = { error: true };
              } else {
                throw new Error(message);
              }
            }
          } else {
            console.error("Purchase Ticket Error: ", message);
            throw new Error(message);
          }
        }
      });

      // Step 2: Simulate network delay for approval completion
      await executeStep(1, () => simulateNetworkDelay(3000));

      // Step 3: Transaction
      await executeStep(2, async () => {
        const { status, message } = await purchaseDrawTicket(
          walletProvider,
          data,
          connectedAddress,
          walletProvider,
          swssDrawABI
        );
        console.log("message: ", message);
        if (status !== "success") {
          if (message?.includes("user rejected transaction")) {
            toast.error("User rejected transaction", {
              style: { fontSize: "14px" },
            });
            closeModal();
            result = { error: true };
          } else {
            if (message?.includes("cannot estimate gas")) {
              toast.error("Insufficient funds to purchase ticket", {
                style: { fontSize: "14px" },
              });
              closeModal();
              result = { error: true };
            } else {
              throw new Error(message);
            }
          }
        }
      });

      if (result.error === false) {
        // Step 4: Minting NFT
        await executeStep(3, () => simulateNetworkDelay(3000));

        toast.success("Ticket purchased successfully!", {
          style: { fontSize: "14px" },
        });

        // Step 5: Completed Purchase
        await executeStep(4, () => simulateNetworkDelay(3000));
        handleSuccess();
      }
    } catch (error) {
      console.error("Error:", error.message || error);
      toast.error(error.message || "An error occurred. Please try again.", {
        style: { fontSize: "14px" },
      });
    } finally {
      closeModal();
      clearInterval(timerInterval);
    }
  };

  const totalTicketPrice = useMemo(() => {
    if (configData?.ticketPrice) {
      return ticketQty * configData?.ticketPrice;
    } else {
      return 0;
    }
  }, [ticketQty, configData.ticketPrice]);

  const calculateTimeLeft = () => {
    const now = new Date().getTime();
    const startDateTxt = dayjs(configData.startTime);
    let inputDate = startDateTxt.isAfter(now)
      ? configData.startTime
      : configData.endTime;

    const distance = new Date(inputDate).getTime() - now;

    if (distance <= 0 && inputDate != null) {
      setIsExpired(true);
    }
    if (distance > 0) {
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      setIsExpired(false);
      setTimeLeft({ days, hours, minutes, seconds });
    } else {
      setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    }
  };

  const handleSuccess = () => {
    window.location.href = "/dashboard/tickets";
    setTimeout(() => {
      closeModal();
    }, [3000]);
  };

  const RemainingTimeBox = ({ title, value }) => {
    return (
      <Box sx={styles.remainingTimeBox}>
        <Typography sx={styles.remainingTimeBoxValueText}>{value}</Typography>
        <Typography sx={styles.remainingTimeBoxText}>{title}</Typography>
      </Box>
    );
  };

  return (
    <Box sx={styles.pageContent}>
      <Box sx={styles.drawTitleTicket}>
        <Stack direction={"column"} spacing={1}>
          <Typography sx={styles.letsJoinText}>
            Lets Join the{" "}
            <span style={styles.letsJoinTextHighlight}>Latest</span>
          </Typography>
          <Stack direction={"row"} alignItems={"center"} spacing={1}>
            <Typography sx={styles.letsJoinText}>
              <span style={styles.letsJoinTextHighlight}>Draw</span> and Win
            </Typography>
            <img
              src={Crown}
              alt="crown"
              style={matches ? styles.crownImage : styles.crownSMImage}
            />
          </Stack>
        </Stack>

        <Box sx={styles.drawNumber}>
          <Typography sx={styles.drawNumberText}>
            Draw Code -{" "}
            {`${drawDetailsModule?.drawName}-${drawDetailsModule.drawId}`}
          </Typography>
        </Box>
      </Box>
      <Box sx={styles.ticketContainer}>
        <Box sx={styles.ticketCard}>
          <Box sx={styles.tickerContent}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              spacing={{ xs: 2, md: 4, lg: 5 }}
            >
              <Stack
                direction={"column"}
                alignItems={"flex-start"}
                justifyContent={"flex-start"}
              >
                <Typography sx={styles.daysStartEndText}>
                  Competition start
                </Typography>
                <Typography sx={styles.daysStartEndTextHighlight}>
                  {/* {iSOToDateTime(configData.startTime)}
                   */}
                  {ticketDateFormat(drawData?.startTime)}
                </Typography>
              </Stack>
              <Stack
                direction={"column"}
                alignItems={"flex-end"}
                justifyContent={"flex-start"}
              >
                <Typography sx={styles.daysStartEndText}>
                  Competition end
                </Typography>
                <Typography sx={styles.daysStartEndTextHighlight}>
                  {/* {iSOToDateTime(targetDate)}
                   */}
                  {ticketDateFormat(drawData?.endTime)}
                </Typography>
              </Stack>
            </Stack>
            <Divider sx={{ my: { xs: 2, sm: 2, lg: 4 } }} />
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={"center"}
              justifyContent={{
                xs: "flex-start",
                md: "space-between",
              }}
              spacing={{ xs: 1, md: 2, lg: 2 }}
            >
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Box
                  sx={{
                    flexDirection: "column",
                    alignItems: "flex-end",
                  }}
                >
                  <Typography sx={styles.perEntrySmallText}>
                    Per Entry
                  </Typography>
                  <Typography sx={styles.perEntryText}>
                    {/* {drawDetails.currencyTicker} {drawDetails.pricePerTicket} */}
                    {`${
                      drawData?.currencyTicker
                        ? drawData?.currencyTicker
                        : "USDT"
                    } ${drawData?.ticketPrice}`}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                  }}
                >
                  <Typography sx={styles.perEntrySmallText}>
                    Blockchain
                  </Typography>
                  <Typography sx={styles.perEntryText}>
                    {drawDetailsModule?.networkName}
                  </Typography>
                </Box>

                <img
                  src={drawDetailsModule.iconUrl}
                  alt="Network Images"
                  style={{ width: 30, height: 30 }}
                />
              </Stack>
            </Stack>

            <Box>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                spacing={1}
                mt={{ xs: 2, lg: 4 }}
              >
                <Typography sx={styles.ticketValues}>0</Typography>
                <Typography sx={styles.ticketValues}>
                  Tickets Sold - {soldTickets}
                </Typography>
                <Typography sx={styles.ticketValues}>{""}</Typography>
              </Stack>
              <Box sx={styles.progressBarContainer}>
                <Box
                  sx={{
                    ...styles.progressBar,
                    width: `${progressBarWidth}%`,
                  }}
                ></Box>
              </Box>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                spacing={1}
              >
                <Typography sx={styles.ticketValues}>
                  {remainingTickets} Tickets Remaining
                </Typography>

                <Typography sx={styles.ticketValues}>
                  {configData.maxParticipants || drawData.totalTickets || 0}
                </Typography>
              </Stack>
              <Divider
                sx={{
                  mt: { xs: 5, lg: 12 },
                  mb: { xs: 2, lg: 2 },
                  mx: 1,
                  border: "1px dashed #D9D9D9",
                }}
              />
              {isExpired || isFutureDate ? (
                <></>
              ) : (
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  spacing={1}
                  justifyContent={"space-between"}
                  sx={{ position: "relative", zIndex: 99 }}
                >
                  <Typography sx={styles.totalTicketValues}>
                    Total{" "}
                    <span style={styles.totalTicketValueText}>
                      {drawData.currencyTicker} {formatNumber(totalTicketPrice)}
                    </span>
                  </Typography>
                  <TextField
                    size="small"
                    variant="outlined"
                    enabled={false}
                    sx={styles.quantityFieldStyle}
                    slotProps={{
                      input: {
                        readOnly: true,
                        startAdornment: (
                          <IconButton
                            sx={styles.quantityFieldMinusIconStyle}
                            onClick={() => {
                              if (Number(ticketQty) > 1) {
                                setTicketQty((prev) => Number(prev) - 1);
                              }
                            }}
                          >
                            <MinusIcon width={12} height={12} color="#fff" />
                          </IconButton>
                        ),
                        endAdornment: (
                          <IconButton
                            sx={styles.quantityFieldPlusIconStyle}
                            onClick={() => {
                              if (Number(ticketQty) < 50) {
                                setTicketQty((prev) => Number(prev) + 1);
                              }
                            }}
                          >
                            <PlusIcon width={12} height={12} color="#fff" />
                          </IconButton>
                        ),
                      },
                    }}
                    value={ticketQty}
                    onChange={(e) => setTicketQty(e.target.value)}
                  />
                </Stack>
              )}

              <Stack
                direction={"row"}
                alignItems={"center"}
                spacing={1}
                justifyContent={"space-between"}
                mt={{ xs: 2, lg: 1.5 }}
                sx={{ position: "relative", zIndex: 99 }}
              >
                <Stack
                  direction={"column"}
                  alignItems={"center"}
                  spacing={{ xs: 0, lg: 1 }}
                  sx={{
                    "&:hover": {
                      cursor: "pointer",
                    },
                  }}
                >
                  <a
                    href={
                      drawData?.explorerBaseUrl +
                      "/address/" +
                      contractAddress+"#code"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={styles.smartContractText}>
                      Smart Contract Link
                    </Typography>
                    <NewTabIcon />
                  </a>
                   <a
                    href={
                      drawData?.explorerBaseUrl +
                      "/address/" +
                      drawData?.tokenAddress
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={styles.smartContractText}>
                     USDT Link
                    </Typography>
                    <NewTabIcon />
                  </a>
                </Stack>
                {isExpired || isFutureDate ? (
                  <Typography
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: "bold",
                      fontSize: "1em",
                    }}
                  >
                    {isFutureDate ? "Launching Soon" : "Completed"}
                  </Typography>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      sx={styles.purchaseButton}
                      onClick={handleTicketPurchase}
                    >
                      Purchase Now
                    </Button>
                    <StepsModel
                      drawDetails={drawData}
                      steps={progressData.steps}
                      currentStep={progressData.currentStep}
                      timer={progressData.timer}
                      open={isModalOpen}
                      onClose={closeModal}
                    />
                  </>
                )}
              </Stack>
            </Box>
          </Box>
          <Box component="img" src={MaskedLogo} sx={styles.ticketLogoImage} />
        </Box>
      </Box>
      {isExpired ? (
        <></>
      ) : (
        <Box sx={styles.daysRemaining}>
          <Typography sx={styles.titleText}>
            Days <span style={styles.titleHighLight}>Remaining</span>
          </Typography>
          <Box sx={styles.daysRemainingTimeContainer}>
            <Box>
              <Typography sx={styles.daysRemainingTimeText}>
                {timeLeft.days}
              </Typography>
              <Typography sx={styles.daysRemainingTimeIcon}>Days</Typography>
            </Box>
            <Box>
              <Typography sx={styles.daysRemainingTimeText}>:</Typography>
            </Box>
            <Box>
              <Typography sx={styles.daysRemainingTimeText}>
                {timeLeft.hours}
              </Typography>
              <Typography sx={styles.daysRemainingTimeIcon}>Hours</Typography>
            </Box>
            <Box>
              <Typography sx={styles.daysRemainingTimeText}>:</Typography>
            </Box>
            <Box>
              <Typography sx={styles.daysRemainingTimeText}>
                {timeLeft.minutes}
              </Typography>
              <Typography sx={styles.daysRemainingTimeIcon}>Minutes</Typography>
            </Box>
            <Box>
              <Typography sx={styles.daysRemainingTimeText}>:</Typography>
            </Box>
            <Box>
              <Typography sx={styles.daysRemainingTimeText}>
                {timeLeft.seconds}
              </Typography>
              <Typography sx={styles.daysRemainingTimeIcon}>Seconds</Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
export default DrawTicket;
